import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';

import { Typography, Tooltip } from '@material-ui/core';
import { useLayoutContext } from 'Context/LayoutContext';

const useStyles = makeStyles(theme => ({
  node: {
    position: 'fixed',
    backgroundColor: theme.palette.primary.main,
    zIndex: theme.zIndex.snackbar,
  },
  nodeBlue: {
    backgroundColor: theme.palette.facet.accentBlue,
  },
  nodeSecondary: {
    backgroundColor: theme.palette.secondary.main,
  },
  nodeRed: {
    backgroundColor: theme.palette.facet.red,
  },
  nodeGrey: {
    backgroundColor: theme.palette.facet.darkGray,
  },
  nodePrimaryGrey: {
    backgroundColor: theme.palette.facet.primaryGray,
  },
  nodeGreen: {
    backgroundColor: theme.palette.facet.accentGreen,
  },
  nodeOrange: {
    backgroundColor: theme.palette.facet.orange,
  },
  nodeGreyText: {
    color: theme.palette.facet.darkGray,
  },
  nodeBlueText: {
    color: theme.palette.facet.accentBlue,
  },
  nodeSecondaryText: {
    color: theme.palette.secondary.main,
  },
  nodeRedText: {
    color: theme.palette.facet.red,
  },
  text: {
    color: theme.palette.common.white,
    fontWeight: theme.typography.fontWeightBold,
    bottom: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    padding: theme.spacing(0.25, 2),
    borderTopRightRadius: theme.radius.sm,
    borderTopLeftRadius: theme.radius.sm,
  },
  horizontalEdge: {
    width: '100%',
    height: theme.spacing(0.25),
  },
  top: {
    top: 0,
  },
  bottom: {
    bottom: 0,
  },
  verticalEdge: {
    height: '100%',
    width: theme.spacing(0.25),
  },
  left: {
    left: 0,
  },
  right: {
    right: 0,
  },
  tooltip: {
    ...theme.typography.h6,
    color: theme.palette.common.white,
    fontWeight: theme.typography.fontWeightBold,
  },
  arrow: {},
}));

function BorderIndicator(props) {
  const {
    color, text, popover, popoverDisabled,
  } = props;
  const { isFocusedLayout } = useLayoutContext();

  const classes = useStyles();
  const nodeClass = classNames(classes.node, {
    [classes.nodeSecondary]: color === 'secondary',
    [classes.nodeBlue]: color === 'blue',
    [classes.nodeRed]: color === 'red',
    [classes.nodeGrey]: color === 'grey',
    [classes.nodeGreen]: color === 'green',
    [classes.nodeOrange]: color === 'orange',
    [classes.nodePrimaryGrey]: color === 'primaryGrey',
  });

  if (isFocusedLayout) {
    return null;
  }

  return (
    <>
      <Tooltip
        title={popover}
        placement="top"
        arrow
        disableHoverListener={popoverDisabled}
        classes={{
          tooltip: classNames(classes.tooltip, {
            [classes.nodeSecondary]: color === 'secondary',
            [classes.nodeBlue]: color === 'blue',
            [classes.nodeRed]: color === 'red',
            [classes.nodeGrey]: color === 'grey',
          }),
          arrow: classNames({
            [classes.nodeSecondaryText]: color === 'secondary',
            [classes.nodeBlueText]: color === 'blue',
            [classes.nodeRedText]: color === 'red',
            [classes.nodeGreyText]: color === 'grey',
          }),
        }}
      >
        <Typography
          variant="h6"
          className={classNames(nodeClass, classes.text)}
        >
          {text}
        </Typography>
      </Tooltip>
      <div
        className={classNames(nodeClass, classes.horizontalEdge, classes.top)}
      />
      <div
        className={classNames(
          nodeClass,
          classes.horizontalEdge,
          classes.bottom,
        )}
      />
      <div
        className={classNames(nodeClass, classes.verticalEdge, classes.left)}
      />
      <div
        className={classNames(nodeClass, classes.verticalEdge, classes.right)}
      />
    </>
  );
}

BorderIndicator.propTypes = {
  color: PropTypes.oneOf([
    'primary',
    'secondary',
    'blue', // Accent Blue
    'red', // Red (#F00)
    'grey', // dark grey
    'green', // Accent green
    'primaryGrey', // primary grey
  ]).isRequired,
  text: PropTypes.string.isRequired,
  popoverDisabled: PropTypes.bool,
};

export default BorderIndicator;
